// src/contexts/AuthContext.tsx

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { jwtDecode } from "jwt-decode";

type clientFlagsType = {
  show_tasks: boolean;
  show_decisions: boolean;
};

type Client = {
  id: number;
  name: string;
  // logo: string | null;
};

type User = {
  id: number;
  username: string;
  role: string;
  permissions: string[];
  client_id: number;
  client_name: string;
  exp: number;
  client: Client | null;
  clientFlags: clientFlagsType | null;
};

type AuthContextType = {
  token: string | null;
  refreshToken: () => Promise<string | null>;
  user: User | null;
  setUser: (user: User | Partial<User>) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  client: Client | null;
  clientFlags: clientFlagsType | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ---- Helper: Schedule auto-logout ----
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Helper: Schedule silent refresh ----
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLogoutTimer = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  // Decode token and set user state
  const setAuthFromToken = useCallback((jwt: string) => {
    const decoded = jwtDecode<User>(jwt);
    setToken(jwt);
    setUser(decoded);
    localStorage.setItem("authToken", jwt);
    return decoded;
  }, []);

  // ---- Logout logic ----
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("authToken");
    clearLogoutTimer();
    clearRefreshTimer();
  }, []);

  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  const scheduleRetry = (delay = 15000) => {
    clearRefreshTimer();
    refreshTimerRef.current = setTimeout(() => refreshToken(), delay);
  };

  // ---- Silent refresh ----
  const refreshToken = async (): Promise<string | null> => {
    try {
      const csrfToken = getCookie('csrf_refresh_token');
      
      const res = await fetch('/api/token/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': csrfToken || '',
        },
      });

      if (!res.ok) {
        console.error(`Refresh failed with status ${res.status}`);
        if (res.status === 401 || res.status === 403) {
          logout();
          return null;
        }
        scheduleRetry(10000);
        return null;
      }

      const data = await res.json();
      const newToken = data.access_token_cookie;
      
      if (!newToken) {
        console.error('No token in refresh response');
        scheduleRetry(10000);
        return null;
      }
      login(newToken);
      return newToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      scheduleRetry(15000);
      return null;
    }
  };

  // ---- Login logic ----
  const login = useCallback((jwt: string) => {
    const decoded = setAuthFromToken(jwt);
    const expiresIn = decoded.exp * 1000 - Date.now();

    clearLogoutTimer();
    clearRefreshTimer();

    logoutTimerRef.current = setTimeout(() => logout(), expiresIn);

    const refreshDelay = Math.max(expiresIn - 2 * 60 * 1000, 30 * 1000);
    
    if (refreshDelay > 0) {
      refreshTimerRef.current = setTimeout(() => refreshToken(), refreshDelay);
    }
  }, [setAuthFromToken, logout, refreshToken]);


  // ---- Permission checker ----
  const hasPermission = useCallback(
    (permission: string) => user?.permissions?.includes(permission) || false,
    [user]
  );

  // ---- Init: check for existing token ----
  useEffect(() => {
    const savedToken = localStorage.getItem("authToken");
    if (!savedToken) {
      setIsLoading(false);
      return;
    }
    try {
      const decoded = jwtDecode<User>(savedToken);
      if (decoded.exp * 1000 > Date.now()) {
        login(savedToken);
      } else {
        logout();
      }
    } catch {
      logout();
    }
    setIsLoading(false);
    // Cleanup timers on unmount
    return () => {
      clearLogoutTimer();
      clearRefreshTimer();
    };
    // eslint-disable-next-line
  }, []);

  const updateUser = useCallback((updatedUser: User | Partial<User>) => {
    setUser(currentUser => {
      if (!currentUser) {
        return updatedUser as User;
      }

      return {
        ...currentUser,
        ...updatedUser,
      };
    });
  }, []);

  const isAuthenticated = !!token && !!user;

  return (
    <AuthContext.Provider value={{
      token,
      user,
      setUser: updateUser,
      login,
      logout,           // <-- make sure this is here!
      refreshToken,     // <-- and this!
      isAuthenticated,
      isLoading,
      hasPermission,    // <-- add this!
      client: user?.client || null,
      clientFlags : user?.clientFlags || null,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  console.log('Context: ', ctx);
  return ctx;
}